import { useState, useMemo } from 'react';
import { useBakimTakip, BakimliMusteri } from './hooks/useBakimTakip';
import { bakimHesapla, BakimBilgi } from './utils/bakimHesapla';
import BakimOzetKartlar from './components/BakimOzetKartlarSupabase';
import BakimMusteriKartiSupabase from './components/BakimMusteriKartiSupabase';

type FilterType = 'hepsi' | 'yesil' | 'sari' | 'turuncu' | 'kirmizi';
type SortType = 'enEski' | 'enYeni' | 'alfabetik';

const sortOptions: { key: SortType; label: string }[] = [
  { key: 'enEski', label: 'En Eski Bakım' },
  { key: 'enYeni', label: 'En Yeni Bakım' },
  { key: 'alfabetik', label: 'Alfabetik' },
];

interface EnrichedMusteri extends BakimliMusteri {
  bakimBilgi: BakimBilgi;
}

export default function BakimTakipPage() {
  const { musteriler, loading, error, refetch } = useBakimTakip();
  const [filter, setFilter] = useState<FilterType>('hepsi');
  const [sort, setSort] = useState<SortType>('enEski');
  const [search, setSearch] = useState('');

  const enriched: EnrichedMusteri[] = useMemo(() =>
    musteriler.map(m => ({
      ...m,
      bakimBilgi: m.son_bakim_tarihi ? bakimHesapla(m.son_bakim_tarihi) : {
        ayFarki: 0, gunFarki: 0, durum: 'yesil' as const,
        etiket: 'Bakım Yok', aciklama: 'Henüz bakım kaydı yok', progressYuzde: 0,
        altiAyTarihi: '', dokuzAyTarihi: '', onikiAyTarihi: '',
      },
    }))
  , [musteriler]);

  const filtered = useMemo(() => {
    let list = enriched;
    if (filter !== 'hepsi') list = list.filter(c => c.bakimBilgi.durum === filter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(c =>
        `${c.ad} ${c.soyad}`.toLowerCase().includes(s) ||
        c.telefon.includes(s) ||
        (c.ilce || '').toLowerCase().includes(s)
      );
    }
    list = [...list].sort((a, b) => {
      if (sort === 'enEski') return b.bakimBilgi.gunFarki - a.bakimBilgi.gunFarki;
      if (sort === 'enYeni') return a.bakimBilgi.gunFarki - b.bakimBilgi.gunFarki;
      return `${a.ad} ${a.soyad}`.localeCompare(`${b.ad} ${b.soyad}`, 'tr');
    });
    return list;
  }, [enriched, filter, sort, search]);

  const turuncuList = enriched.filter(c => c.bakimBilgi.durum === 'turuncu');
  const kirmiziList = enriched.filter(c => c.bakimBilgi.durum === 'kirmizi');

  const ozetMusteriler = enriched.map(m => ({
    id: m.id,
    adSoyad: `${m.ad} ${m.soyad}`,
    telefon: m.telefon,
    ilce: m.ilce,
    il: m.il,
    cihazTuru: m.cihaz_turu,
    sonBakimTarihi: m.son_bakim_tarihi || new Date().toISOString(),
  }));

  if (loading) {
    return (
      <div className="text-center py-16">
        <i className="ri-loader-4-line animate-spin text-3xl text-sky-500"></i>
        <p className="text-sm text-gray-500 mt-3">Bakım verileri yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
        <p className="text-sm text-red-700 font-medium">Veriler yüklenemedi.</p>
        <button onClick={refetch} className="mt-3 text-sm text-red-600 font-semibold cursor-pointer">Tekrar Dene</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BakimOzetKartlar customers={ozetMusteriler} onFilter={setFilter} aktifFilter={filter} />

      {kirmiziList.length > 0 && filter === 'hepsi' && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 flex items-center gap-3">
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            <i className="ri-alarm-warning-line text-red-500 text-lg"></i>
          </div>
          <p className="text-sm text-red-800 font-medium">
            <span className="font-bold">{kirmiziList.length} müşterinizin</span> bakımı 12 ayı geçmiş!
          </p>
          <button onClick={() => setFilter('kirmizi')} className="ml-auto text-xs bg-red-200 hover:bg-red-300 text-red-800 font-semibold px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors">Görüntüle</button>
        </div>
      )}

      {turuncuList.length > 0 && filter === 'hepsi' && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-5 py-3 flex items-center gap-3">
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            <i className="ri-alert-line text-orange-500 text-lg"></i>
          </div>
          <p className="text-sm text-orange-800 font-medium">
            <span className="font-bold">{turuncuList.length} müşterinizin</span> bakım süresi gecikiyor.
          </p>
          <button onClick={() => setFilter('turuncu')} className="ml-auto text-xs bg-orange-200 hover:bg-orange-300 text-orange-800 font-semibold px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors">Görüntüle</button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-sky-400 bg-white"
              placeholder="Müşteri veya ilçe ara..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select value={sort} onChange={e => setSort(e.target.value as SortType)}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-sky-400 bg-white cursor-pointer">
            {sortOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500 bg-white border border-gray-100 rounded-xl px-3 py-2.5">
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500 flex-shrink-0"></span>0-6 ay</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-yellow-400 flex-shrink-0"></span>6-9 ay</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-orange-500 flex-shrink-0"></span>9-12 ay</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0"></span>12+ ay</div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-600">
          {filtered.length} müşteri gösteriliyor
          {filter !== 'hepsi' && (
            <button onClick={() => setFilter('hepsi')} className="ml-2 text-sky-600 hover:text-sky-800 cursor-pointer font-medium">× Filtreyi temizle</button>
          )}
        </h3>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-search-line text-2xl text-slate-400"></i>
          </div>
          <p className="text-sm text-gray-500">
            {search ? 'Seçilen filtrelere uygun kayıt bulunamadı.' : 'Bakım zamanı gelen müşteri bulunmuyor.'}
          </p>
          {search && (
            <button onClick={() => { setFilter('hepsi'); setSearch(''); }} className="mt-3 text-sky-600 text-sm font-medium cursor-pointer hover:text-sky-800">Tüm müşterileri göster</button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(c => (
            <BakimMusteriKartiSupabase key={c.id} customer={c} />
          ))}
        </div>
      )}
    </div>
  );
}